/**
 * Ranger select (08 §2.4): the company as dossier cards; unavailable rangers stay on the grid,
 * greyed, with the reason. The first time, the permadeath contract is stated where it cannot be
 * missed (04 §3); afterwards the same sentence rides in the footer.
 */

import './rangerSelect.css';
import type { RangerRecord } from '../../meta/types';
import type { Ctx, MissionPlan, Screen } from './context';
import { box, button, el, hinted } from './dom';
import { availability, RP_MEANING, serviceName } from './text';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';
import { openServiceFile } from './widgets/serviceFileDialog';
import { NAV_HINTS } from './widgets/keyHints';

/** The contract with the player, in one sentence (08 §2.4). */
export const PERMADEATH_LINE = 'A RANGER WHO DIES ON A MISSION IS GONE FOR GOOD.';

const PERMADEATH_RULES = [
  'CAPTURED IS NOT DEAD: A PRISONER CAN BE BROUGHT BACK BY A POW RESCUE - ONE ATTEMPT.',
  'RECALL IS THE ABORT: A FAILED MISSION COSTS READINESS, NEVER THE RANGER. YOU STILL HAVE TO REACH THE LADDER.',
  'ONCE THE AIRCRAFT LEAVES THERE IS NO WAY TO UN-DEPLOY HIM.',
];

/** The dead and the gone are not candidates; everyone else is shown, deployable or not. */
function onTheBooks(r: RangerRecord): boolean {
  return r.status !== 'kia' && r.status !== 'lost' && r.status !== 'retired';
}

function permadeathBanner(): HTMLElement {
  const banner = el('div', 'menu-perma');
  banner.setAttribute('role', 'alert');
  banner.append(el('div', 'menu-perma-line', PERMADEATH_LINE));
  for (const rule of PERMADEATH_RULES) banner.append(el('div', 'menu-perma-rule menu-prose', rule));
  return banner;
}

export function rangerSelectScreen(ctx: Ctx, plan: MissionPlan): Screen {
  const op = ctx.api.profile().operation;
  const seen = ctx.settings.get().permadeathSeen;
  const f = frame('rangers', 'SELECT RANGER', NAV_HINTS, seen ? PERMADEATH_LINE : '');
  f.info.append(el('span', '', ctx.describe(plan.params.archetype).name));
  if (op !== null) f.info.append(hinted('', `RP ${op.rp}`, RP_MEANING));

  const back = (): void => ctx.nav.briefing(plan);
  /** SELECT, inside his file: having read it, send him without shutting it first. */
  const fileSelect = (r: RangerRecord): HTMLButtonElement => {
    const pick = button('SELECT', () => {
      ctx.closeModal();
      select(r);
    }, 'menu-btn-small');
    pick.setAttribute('aria-label', `SELECT ${serviceName(r)}`);
    return pick;
  };
  const select = (r: RangerRecord): void => {
    // He has now been told; from here on the line rides in the footer.
    ctx.settings.set({ permadeathSeen: true });
    ctx.nav.loadout({ ...plan, rangerId: r.id });
  };

  const company = (op?.company ?? []).filter(onTheBooks);
  const grid = el('div', 'menu-rangers-grid');
  let first: HTMLButtonElement | null = null;
  for (const r of company) {
    const card = dossierCard(r, { describe: ctx.describe });
    // The card says what he IS; his file says what he has DONE - which mission won him that ribbon,
    // and which one he did not come home clean from. Asked for in play, at exactly this moment:
    // the choice of who to send is the one place it matters most.
    const openFile = (): void => openServiceFile(ctx, r, availability(r).available
      ? fileSelect(r)
      : undefined);
    const actions = box('menu-dossier-actions', button('FILE', openFile, 'menu-btn-small'));
    if (availability(r).available) {
      const pick = button('SELECT', () => select(r), 'menu-btn-small');
      pick.setAttribute('aria-label', `SELECT ${serviceName(r)}`);
      actions.append(pick);
      first ??= pick;
    }
    card.append(actions);
    grid.append(card);
  }

  const column = el('div', 'menu-col menu-grow');
  if (!seen) column.append(permadeathBanner());
  if (first === null) {
    column.append(
      el(
        'div',
        'menu-error menu-prose',
        'NO RANGER CAN DEPLOY. WOUNDED MEN RETURN AS MISSIONS PASS; A REPLACEMENT DEPOT CAN RECRUIT.'
          + ' WITH NOBODY LEFT AND NO WAY TO RECRUIT, THE OPERATION IS LOST.',
      ),
    );
  }
  const backButton = button('BACK', back);
  column.append(box('menu-scroll menu-grow', grid), box('menu-row menu-rangers-actions', backButton));
  f.body.append(column);

  return {
    el: f.el,
    back,
    initialFocus: () => first ?? backButton,
  };
}
